import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Leaf, Building2, Scissors, ArrowRight, Check } from "lucide-react";
import { useSEO } from "../hooks/useSEO";

const FadeInSection = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay }}
    >
      {children}
    </motion.div>
  );
};

const services = [
  {
    icon: Leaf,
    title: "Diseño Naturalista Integral",
    titleEn: "Integral Naturalist Design",
    description: "Plan maestro evolutivo adaptado a tu propiedad. Incluye estudio técnico, selección vegetal y diseño de espacios exteriores.",
    descriptionEn: "Evolutionary master plan adapted to your property. Includes technical study, plant selection, and outdoor space design.",
    ideal: "Propietarios que buscan transformación completa",
    idealEn: "Homeowners seeking complete transformation"
  },
  {
    icon: Building2,
    title: "Proyectos Estratégicos",
    titleEn: "Strategic Projects",
    description: "Intervenciones de alto impacto para desarrollos. Cronograma claro, presupuesto estructurado, ejecución eficiente.",
    descriptionEn: "High-impact interventions for developments. Clear timeline, structured budget, efficient execution.",
    ideal: "Desarrolladores y proyectos comerciales",
    idealEn: "Developers and commercial projects"
  },
  {
    icon: Scissors,
    title: "Cuidados del Jardín",
    titleEn: "Garden Care",
    description: "Mantenimiento profesional con visitas programadas. Tu jardín siempre impecable, tú sin preocupaciones.",
    descriptionEn: "Professional maintenance with scheduled visits. Your garden always impeccable, you worry-free.",
    ideal: "Propietarios que valoran su inversión",
    idealEn: "Homeowners who value their investment"
  }
];

const maintenancePlans = [
  {
    id: "consultoria_suelo",
    name: "Consultoría de Suelo",
    nameEn: "Soil Consultation",
    price: "$7,000 MXN",
    period: "única vez",
    periodEn: "one-time",
    description: "Estudio técnico inicial del sitio. Análisis de suelo, drenaje, exposición solar y microclima.",
    descriptionEn: "Initial technical site study. Soil, drainage, sun exposure, and microclimate analysis.",
    features: [
      { es: "Análisis técnico del microclima", en: "Microclimate technical analysis" },
      { es: "Estudio de suelo y drenaje", en: "Soil and drainage study" },
      { es: "Evaluación solar y de vientos", en: "Sun and wind evaluation" },
      { es: "Recomendaciones de vegetación", en: "Vegetation recommendations" },
      { es: "Descontable si avanzas a proyecto", en: "Deductible if you proceed to project" },
    ],
    highlight: true,
    highlightText: "Punto de partida recomendado",
    highlightTextEn: "Recommended starting point",
  },
  {
    id: "mantenimiento_basico",
    name: "Mantenimiento Básico",
    nameEn: "Basic Maintenance",
    price: "$4,500 MXN",
    period: "/mes",
    periodEn: "/month",
    description: "Visitas quincenales para mantener tu jardín en óptimas condiciones.",
    descriptionEn: "Biweekly visits to keep your garden in optimal condition.",
    features: [
      { es: "Visitas cada 15 días", en: "Visits every 15 days" },
      { es: "Poda y limpieza general", en: "General pruning and cleanup" },
      { es: "Revisión de sistema de riego", en: "Irrigation system check" },
      { es: "Fertilización básica", en: "Basic fertilization" },
      { es: "Reporte con fotos", en: "Photo report" },
    ],
    highlight: false,
  },
  {
    id: "mantenimiento_premium",
    name: "Mantenimiento Premium",
    nameEn: "Premium Maintenance",
    price: "$8,500 MXN",
    period: "/mes",
    periodEn: "/month",
    description: "Visitas semanales con cuidado avanzado y atención prioritaria.",
    descriptionEn: "Weekly visits with advanced care and priority attention.",
    features: [
      { es: "Visitas semanales", en: "Weekly visits" },
      { es: "Poda técnica especializada", en: "Specialized technical pruning" },
      { es: "Sistema de riego optimizado", en: "Optimized irrigation system" },
      { es: "Fertilización avanzada", en: "Advanced fertilization" },
      { es: "Control integral de plagas", en: "Comprehensive pest control" },
      { es: "Reporte detallado con fotos", en: "Detailed photo report" },
      { es: "Atención prioritaria", en: "Priority attention" },
    ],
    highlight: false,
  },
];

export default function Planes() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  useSEO({ title: isEn ? "Plans" : "Planes", description: "Planes de mantenimiento y servicios de paisajismo en Los Cabos. Desde $4,500 MXN/mes." });

  return (
    <div className="min-h-screen pt-32 pb-24 bg-[#F9F6F0]" data-testid="planes-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs tracking-[0.3em] uppercase text-circulo-sand mb-4 block"
          >
            {isEn ? "Our Services" : "Nuestros Servicios"}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium mb-6"
          >
            {isEn ? "How we work" : "Cómo trabajamos"}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            {isEn
              ? "Each project is unique. We start with a visit to understand your needs and provide a personalized proposal."
              : "Cada proyecto es único. Comenzamos con una visita para entender tus necesidades y ofrecerte una propuesta personalizada."}
          </motion.p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {services.map((service, index) => (
            <FadeInSection key={index} delay={index * 0.1}>
              <div className="p-8 border border-[#E6E2D6] bg-white h-full flex flex-col">
                <service.icon className="w-10 h-10 text-circulo-sand/60 mb-6" />
                <h3 className="text-2xl font-serif mb-4">
                  {isEn ? service.titleEn : service.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6 flex-grow">
                  {isEn ? service.descriptionEn : service.description}
                </p>
                <p className="text-xs text-circulo-sand uppercase tracking-wider">
                  {isEn ? "Ideal for: " : "Ideal para: "}{isEn ? service.idealEn : service.ideal}
                </p>
              </div>
            </FadeInSection>
          ))}
        </div>

        {/* Pricing Cards */}
        <FadeInSection delay={0.2}>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif mb-4">
              {isEn ? "Plans & Pricing" : "Planes e Inversión"}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {isEn
                ? "Transparent pricing. Start with a soil consultation — the cost is deductible if you proceed to a full project."
                : "Precios transparentes. Comienza con una consultoría de suelo — el costo se descuenta si avanzas a proyecto."}
            </p>
          </div>
        </FadeInSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {maintenancePlans.map((plan, index) => (
            <FadeInSection key={plan.id} delay={index * 0.1}>
              <div className={`p-8 h-full flex flex-col relative ${
                plan.highlight
                  ? "border-2 border-[#547055] bg-white"
                  : "border border-[#E6E2D6] bg-white"
              }`}>
                {plan.highlight && (
                  <span className="absolute -top-3 left-8 bg-[#547055] text-white text-[10px] uppercase tracking-wider px-3 py-1">
                    {isEn ? plan.highlightTextEn : plan.highlightText}
                  </span>
                )}
                <h3 className="text-xl font-serif mb-2">
                  {isEn ? plan.nameEn : plan.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-serif font-medium text-[#1C2E24]">{plan.price}</span>
                  <span className="text-sm text-muted-foreground">
                    {isEn ? plan.periodEn : plan.period}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  {isEn ? plan.descriptionEn : plan.description}
                </p>
                <div className="flex flex-col gap-3 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-[#547055] flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{isEn ? feature.en : feature.es}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contacto" className={`text-center py-3 px-6 text-sm uppercase tracking-wider transition-colors ${
                  plan.highlight
                    ? "bg-[#1C2E24] text-white hover:bg-[#547055]"
                    : "border border-[#1C2E24] text-[#1C2E24] hover:bg-[#1C2E24] hover:text-white"
                }`}>
                  {isEn ? "Get started" : "Comenzar"}
                </Link>
              </div>
            </FadeInSection>
          ))}
        </div>

        {/* Process */}
        <FadeInSection delay={0.3}>
          <div className="border border-[#E6E2D6] bg-white p-8 md:p-12 mb-20">
            <h2 className="text-3xl font-serif mb-8 text-center">
              {isEn ? "Our Process" : "Nuestro Proceso"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <span className="text-4xl font-serif text-circulo-sand/30 block mb-3">01</span>
                <h4 className="font-serif mb-2">{isEn ? "Initial Visit" : "Visita Inicial"}</h4>
                <p className="text-sm text-muted-foreground">
                  {isEn ? "We get to know your property and understand your vision" : "Conocemos tu propiedad y entendemos tu visión"}
                </p>
              </div>
              <div className="text-center">
                <span className="text-4xl font-serif text-circulo-sand/30 block mb-3">02</span>
                <h4 className="font-serif mb-2">{isEn ? "Proposal" : "Propuesta"}</h4>
                <p className="text-sm text-muted-foreground">
                  {isEn ? "Custom proposal with scope, timeline, and investment" : "Propuesta personalizada con alcance, tiempos e inversión"}
                </p>
              </div>
              <div className="text-center">
                <span className="text-4xl font-serif text-circulo-sand/30 block mb-3">03</span>
                <h4 className="font-serif mb-2">{isEn ? "Execution" : "Ejecución"}</h4>
                <p className="text-sm text-muted-foreground">
                  {isEn ? "Phased implementation with constant follow-up" : "Implementación por fases con seguimiento constante"}
                </p>
              </div>
              <div className="text-center">
                <span className="text-4xl font-serif text-circulo-sand/30 block mb-3">04</span>
                <h4 className="font-serif mb-2">{isEn ? "Evolution" : "Evolución"}</h4>
                <p className="text-sm text-muted-foreground">
                  {isEn ? "Your garden grows with you over time" : "Tu jardín crece contigo con el tiempo"}
                </p>
              </div>
            </div>
          </div>
        </FadeInSection>

        {/* CTA */}
        <FadeInSection delay={0.4}>
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-serif mb-6">
              {isEn ? "Ready to start?" : "¿Listo para comenzar?"}
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8">
              {isEn
                ? "Schedule an initial visit. We'll evaluate your property and prepare a proposal tailored to your needs and budget."
                : "Agenda una visita inicial. Evaluamos tu propiedad y preparamos una propuesta a la medida de tus necesidades y presupuesto."}
            </p>
            <Link to="/contacto" className="btn-primary inline-flex items-center gap-2">
              {isEn ? "Contact Us" : "Contáctanos"}
              <ArrowRight size={16} />
            </Link>
          </div>
        </FadeInSection>
      </div>
    </div>
  );
}
